import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { Formik } from "formik";
import { fieldsetProps, useAmsSelector } from "@awe-web/shared/lib";
import { merge } from "@awe-web/shared/lib";
import { Translation as T } from "@awe-web/shared/lib";
import { getRentContractId } from "@awe-web/shared/lib";
import {
  AddressFieldset,
  CreditCardsFieldset,
  IdentificationAndDriversLicenseFieldset
} from "../../../user/components/form.js";
import {
  newValidate,
  newGetInitialValues,
  newIsFieldRequired,
  newGetFieldLabel,
  newGetFieldPlaceholder,
  newGetSelectFieldOptions
} from "@awe-web/shared/lib";
import { Row, Col } from "../../../components/grid.js";
import { Button, ScrollToError } from "../../../components/form.js";
import { getCreditCardsEnabled } from "../../../config/store.js";
import { submit } from "./actions.js";

import "./sign_up_confirmed_form.scss";

const SUBMIT = "FSD0000236";

function InternalSignUpConfirmedForm(props) {
  const { creditCardsEnabled, handleSubmit } = props;

  return (
    <form className="sign-up-confirmed-form" onSubmit={handleSubmit}>
      <ScrollToError />
      <AddressFieldset {...fieldsetProps(props)} />
      {creditCardsEnabled && (
        <CreditCardsFieldset {...fieldsetProps(props)} />
      )}
      <IdentificationAndDriversLicenseFieldset
        className="sign-up-confirmed-form__identification-and-drivers-license-fieldset"
        {...fieldsetProps(props)}
      />

      <Row>
        <Col tablet={{ cols: 4, offset: 4 }} desktop={{ cols: 4, offset: 6 }}>
          <Button primary continue submit className="sign-up-confirmed-form__submit-button"><T code={SUBMIT} /></Button>
        </Col>
      </Row>
    </form>
  );
}

InternalSignUpConfirmedForm.valuesShape = merge(
  AddressFieldset.valuesShape,
  IdentificationAndDriversLicenseFieldset.valuesShape
);
InternalSignUpConfirmedForm.propTypes = {
  creditCardsEnabled: PropTypes.bool,
  values: PropTypes.shape(InternalSignUpConfirmedForm.valuesShape),
  getSelectFieldOptions: PropTypes.func,
  getFieldLabel: PropTypes.func,
  getFieldPlaceholder: PropTypes.func,
  handleChange: PropTypes.func,
  handleBlur: PropTypes.func,
  handleSubmit: PropTypes.func
};

export default function SignUpConfirmedForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const rentContractId = useSelector((state) => getRentContractId(state));
  const creditCardsEnabled = useSelector((state) => getCreditCardsEnabled(state));
  const isFieldRequired = useAmsSelector((state) => newIsFieldRequired("extendRegistration")(state));
  const getFieldLabel = useAmsSelector((state) => newGetFieldLabel("extendRegistration")(state));
  const getFieldPlaceholder = useAmsSelector((state) => newGetFieldPlaceholder("extendRegistration")(state));
  const getSelectFieldOptions = useAmsSelector((state) => newGetSelectFieldOptions("extendRegistration")(state));

  const validate = useAmsSelector((state) => newValidate("extendRegistration")(state));

  const initialValues = useAmsSelector((state) => {
    const formInitializer = newGetInitialValues("extendRegistration");
    const inialValuesFactory = formInitializer(state);
    return inialValuesFactory({
      rentContractId,
      [AddressFieldset.STREET]: "",
      [AddressFieldset.BUILDING]: "",
      [AddressFieldset.ZIP_CODE]: "",
      [AddressFieldset.CITY]: "",
      [AddressFieldset.COUNTRY_CODE]: "",
      [IdentificationAndDriversLicenseFieldset.IDENT_CARD_TYPE]: "",
      [IdentificationAndDriversLicenseFieldset.IDENT_CARD_NO]: "",
      [IdentificationAndDriversLicenseFieldset.IDENT_CARD_COUNTRY]: "",
      [IdentificationAndDriversLicenseFieldset.IDENT_CARD_AUTHORITY]: "",
      [IdentificationAndDriversLicenseFieldset.IDENT_CARD_DATE_ISSUED]: undefined,
      [IdentificationAndDriversLicenseFieldset.IDENT_CARD_VALID_TILL]: undefined,
      [IdentificationAndDriversLicenseFieldset.DRIVERS_LICENSE_NO]: "",
      [IdentificationAndDriversLicenseFieldset.DRIVERS_LICENSE_COUNTRY]: "",
      [IdentificationAndDriversLicenseFieldset.DRIVERS_LICENSE_DATE_ISSUED]: undefined
    });
  });

  const onSubmit = useCallback(async (values, formikProps) => {
    const nextPath = await dispatch(submit(values, formikProps));
    if (nextPath && nextPath.length > 0) {
      navigate(nextPath);
    }
  }, [dispatch, navigate]);

  return (
    <Formik initialValues={initialValues} validate={validate} onSubmit={onSubmit}>
      {(formikProps) => (
        <InternalSignUpConfirmedForm
          {...formikProps}
          creditCardsEnabled={creditCardsEnabled}
          isFieldRequired={isFieldRequired}
          getFieldLabel={getFieldLabel}
          getFieldPlaceholder={getFieldPlaceholder}
          getSelectFieldOptions={getSelectFieldOptions}
        />
      )}
    </Formik>
  );
}
